import { useEffect, useState, useCallback } from "react";
import { Pencil, MapPin, Star } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import AdminHeader from "../../components/admin/AdminHeader";
import SlidePanel from "../../components/admin/SlidePanel";
import {
  TextField, TextareaField, SelectField, CheckboxField, FormFooterActions,
} from "../../components/admin/FormFields";
import ImageUpload from "../../components/admin/ImageUpload";

const EMPTY = {
  id: "",
  title: "",
  category: "residential",
  service_type: "patio",
  location: "Edmonton",
  description: "",
  image_url: "",
  before_image: "",
  after_image: "",
  budget_range: "",
  featured: false,
};

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/projects").then((r) => setItems(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const open = (item) => setEditing(item ? { ...item } : { ...EMPTY });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.title || !editing.location || !editing.image_url) {
      toast.error("Title, Location, and Image are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/projects", editing);
      toast.success("Project saved");
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing.id) return close();
    if (!window.confirm(`Delete "${editing.title}"?`)) return;
    try {
      await api.delete(`/admin/projects/${editing.id}`);
      toast.success("Project deleted");
      close();
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-projects">
      <AdminHeader
        title="Projects"
        subtitle="Manage portfolio. Featured projects appear on the homepage."
        action="Add Project"
        actionTestId="add-project-btn"
        onAction={() => open(null)}
        count={items.length}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <button
            key={p.id}
            data-testid={`project-row-${p.id}`}
            onClick={() => open(p)}
            className="group text-left bg-[#151515] border border-[#D8D6D0]/10 hover:border-[#D8D6D0]/30 transition-colors overflow-hidden"
          >
            <div className="aspect-[4/3] overflow-hidden bg-[#0d0d0d]">
              {p.image_url && (
                <img src={p.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${p.image_url}` : p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="font-serif-display text-xl truncate">{p.title}</div>
                <Pencil className="w-4 h-4 text-[#D8D6D0]/30 group-hover:text-[#F5F3EF] flex-shrink-0" />
              </div>
              <div className="flex items-center gap-3 text-xs text-[#D8D6D0]/50 mt-2">
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {p.location}</span>
                <span>·</span>
                <span className="uppercase tracking-widest">{p.category}</span>
                {p.featured && (
                  <span className="inline-flex items-center gap-1 text-[#8C1D30] ml-auto">
                    <Star className="w-3 h-3 fill-current" /> Featured
                  </span>
                )}
              </div>
              <p className="text-xs text-[#D8D6D0]/55 mt-3 line-clamp-2">{p.description}</p>
            </div>
          </button>
        ))}
      </div>

      <SlidePanel
        open={!!editing}
        onClose={close}
        title={editing?.id ? "Edit Project" : "New Project"}
        subtitle={editing?.id ? `ID: ${editing.id.slice(0, 8)}…` : "Add a new portfolio piece"}
        footer={
          <FormFooterActions
            onCancel={close}
            onSubmit={save}
            onDelete={editing?.id ? remove : null}
            submitting={saving}
          />
        }
      >
        {editing && (
          <>
            <TextField id="title" label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} required testId="project-title" />
            <SelectField
              id="category"
              label="Category"
              value={editing.category}
              onChange={(v) => setEditing({ ...editing, category: v })}
              options={[
                { value: "residential", label: "Residential" },
                { value: "commercial", label: "Commercial" },
              ]}
              required
            />
            <TextField id="service_type" label="Service Type" value={editing.service_type} onChange={(v) => setEditing({ ...editing, service_type: v })} placeholder="patio, retaining_wall, outdoor_living…" hint="lowercase, underscored. Matches service slugs." />
            <TextField id="location" label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} placeholder="Edmonton" required hint="Must match a Service Area name exactly to appear on that area's page." />
            <TextField id="budget_range" label="Budget Range" value={editing.budget_range} onChange={(v) => setEditing({ ...editing, budget_range: v })} placeholder="$75,000 – $110,000" />
            <TextareaField id="description" label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} rows={4} />
            <ImageUpload label="Main Image" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} required testId="project-image" />
            <ImageUpload label="Before Image (optional)" value={editing.before_image} onChange={(v) => setEditing({ ...editing, before_image: v })} />
            <ImageUpload label="After Image (optional)" value={editing.after_image} onChange={(v) => setEditing({ ...editing, after_image: v })} />
            <CheckboxField id="featured" label="Featured on homepage" value={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} hint="Featured projects appear in the Home page hero grid." />
          </>
        )}
      </SlidePanel>
    </div>
  );
}
